/**
 * CAHPS Entry tab (P7): direct per-SSM n + top-box rate entry (no vendor
 * mapping — owner decision), compute mode, live projected 321 score.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { logicForYear } from '@/lib/app-plus/years/registry';
import type { CahpsSsmEntry } from '@/lib/app-plus-scoring';
import type { UseAppPlusScoring, StoredSubmission } from '../lib/useAppPlusScoring';
import { NextDecileChip, CAHPS_PROJECTION_CAVEAT } from '../lib/cahpsInsights';

interface SsmDraft { n: string; ratePct: string; official: boolean; officialPoints: string }

export function CahpsEntryTab({ scoring }: { scoring: UseAppPlusScoring }) {
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { toast } = useToast();
  // D13: resolve through the year's logic module.
  const logic = useMemo(() => logicForYear(programYear), [programYear]);
  const ssms = useMemo(() => {
    try { return logic.getCahpsSsms(programYear); } catch { return []; }
  }, [logic, programYear]);

  const [drafts, setDrafts] = useState<Record<string, SsmDraft>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const stored = scoring.entries['321']?.payload?.ssmEntries ?? [];
    const next: Record<string, SsmDraft> = {};
    for (const ssm of ssms) {
      const e = stored.find(s => s.ssmId === ssm.ssmId);
      next[ssm.ssmId] = {
        n: e ? String(e.n) : '',
        ratePct: e ? String(e.ratePct) : '',
        official: e?.computeMode === 'cms_official',
        officialPoints: e?.officialPoints != null ? String(e.officialPoints) : '',
      };
    }
    setDrafts(next);
  }, [scoring.entries, ssms]);

  const entriesFromDrafts = (): CahpsSsmEntry[] =>
    ssms.flatMap(ssm => {
      const d = drafts[ssm.ssmId];
      if (!d || d.n === '' || (d.ratePct === '' && !d.official)) return [];
      return [{
        ssmId: ssm.ssmId,
        n: Number(d.n),
        ratePct: d.ratePct === '' ? 0 : Number(d.ratePct),
        computeMode: d.official ? 'cms_official' as const : 'vendor_projected' as const,
        ...(d.official && d.officialPoints !== '' ? { officialPoints: Number(d.officialPoints) } : {}),
      }];
    });

  const preview = useMemo(() => {
    if (!ssms.length) return null;
    try {
      return logic.scoreCahps(entriesFromDrafts(), ssms, logic.getConstants(programYear), true);
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, ssms, logic, programYear]);

  const save = async () => {
    setSaving(true);
    try {
      const row: StoredSubmission = {
        measure_id: '321',
        collection_type: 'CAHPS Survey Vendor',
        reported: true,
        payload: { ssmEntries: entriesFromDrafts() },
      };
      await scoring.save([row]);
      toast({ title: 'CAHPS entries saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const set = (ssmId: string, patch: Partial<SsmDraft>) =>
    setDrafts(prev => ({ ...prev, [ssmId]: { ...prev[ssmId], ...patch } }));

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">CAHPS Summary Survey Measures</CardTitle>
          <CardDescription>
            Raw top-box rates from your survey vendor — unadjusted, so scores are PROJECTED
            until CMS's official values arrive. SSMs under 20 respondents drop from the average.
          </CardDescription>
        </div>
        {preview && preview.state === 'scored' && (
          <div className="text-right">
            <div className="text-3xl font-bold tabular-nums">{preview.points.toFixed(1)}</div>
            <Badge variant={preview.displayState === 'official' ? 'default' : 'secondary'}>
              {preview.displayState.toUpperCase()} · {preview.scoredSsmCount} SSMs
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SSM</TableHead>
              <TableHead className="w-28">n</TableHead>
              <TableHead className="w-32">Top-box rate %</TableHead>
              <TableHead className="w-28">CMS official?</TableHead>
              <TableHead className="w-32">Official points</TableHead>
              <TableHead className="text-right">Projected pts</TableHead>
              <TableHead className="text-right">40th-pct floor</TableHead>
              <TableHead className="text-center">Next decile</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ssms.map(ssm => {
              const d = drafts[ssm.ssmId] ?? { n: '', ratePct: '', official: false, officialPoints: '' };
              const r = preview?.ssmResults.find(s => s.ssmId === ssm.ssmId);
              return (
                <TableRow key={ssm.ssmId}>
                  <TableCell className="font-medium">{ssm.title}</TableCell>
                  <TableCell>
                    <Input type="number" min={0} className="h-8" disabled={isCurrentYearLocked}
                      value={d.n} onChange={e => set(ssm.ssmId, { n: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={0} max={100} step="0.01" className="h-8" disabled={isCurrentYearLocked}
                      value={d.ratePct} onChange={e => set(ssm.ssmId, { ratePct: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Switch checked={d.official} disabled={isCurrentYearLocked}
                      onCheckedChange={v => set(ssm.ssmId, { official: v })} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={0} max={10} step="0.1" className="h-8"
                      disabled={isCurrentYearLocked || !d.official}
                      value={d.officialPoints} onChange={e => set(ssm.ssmId, { officialPoints: e.target.value })} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r?.scored ? r.points?.toFixed(1) : (d.n !== '' && Number(d.n) < 20 ? <Badge variant="outline">n&lt;20 drops</Badge> : '—')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r?.fortiethPercentileFloor != null ? `${r.fortiethPercentileFloor.toFixed(2)}%` : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {/* Decile-proximity insight (owner-approved 2026-07-20) */}
                    {r?.scored && !d.official
                      ? <NextDecileChip ratePct={r.ratePct} row={ssm.row} />
                      : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="mt-4 flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">{CAHPS_PROJECTION_CAVEAT}</p>
          <Button onClick={save} disabled={saving || isCurrentYearLocked}>
            {saving ? 'Saving…' : 'Save CAHPS entries'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
